/**
 * perf.ts — recipe-open boundary instrumentation.
 *
 * Wave PERF-1. Attributes the operator-reported "recipes load for a few
 * seconds" symptom to a specific phase by stamping `performance.mark()` and
 * `performance.measure()` at fixed boundaries:
 *
 *   recipe.routeEnter        — route param parse begins (URL → :slug match)
 *   recipe.rpcStart          — first RPC for the recipe is dispatched
 *   recipe.rpcEnd            — RPC roundtrip resolved (recipe.list / recipe.get)
 *   recipe.workspaceMount    — Workspace component first runs
 *   recipe.editorMount       — TipTap editor instance is non-null
 *   recipe.grammarFirstParse — first evaluator result returned from the worker
 *   recipe.firstPaint        — first rAF after content has been written into the DOM
 *
 * The marks align conceptually with the backend `Server-Timing` headers added
 * in parallel (Wave PERF-1, backend agent) so a roundtrip can be split into
 * client-attributable and server-attributable spans.
 *
 * Activation:
 *   - Active when `import.meta.env.DEV` is true OR `import.meta.env.VITE_PERF_PROFILE === "1"`.
 *   - All mark/measure calls become no-ops in production builds, and the
 *     `performance` API access is short-circuited so the cost is one boolean
 *     check per call site.
 *
 * Headless capture:
 *   When active, `window.__perf` is exposed:
 *     window.__perf.entries() — current marks + measures
 *     window.__perf.reset()   — clear timeline (called on each route nav)
 *     window.__perf.subscribe(fn) — overlay subscription
 *
 * No user-visible string in this module — the overlay is internal-only and
 * the "Instrument" name (design.md §2 internal-only rule) does not appear.
 */

// ── Activation gate ────────────────────────────────────────────────────────────

/**
 * Build-time + URL-time gate. Vite replaces `import.meta.env.DEV` with a
 * literal boolean at build time; in production the entire body of the
 * `if (PERF_ENABLED)` blocks tree-shakes to nothing, so the runtime cost is
 * ~zero outside dev / opt-in profile builds.
 */
export const PERF_ENABLED: boolean = (() => {
  try {
    const env = import.meta.env as Record<string, string | boolean | undefined> | undefined;
    if (!env) return false;
    if (env["DEV"] === true) return true;
    if (env["VITE_PERF_PROFILE"] === "1") return true;
    return false;
  } catch {
    return false;
  }
})();

// ── Mark names (canonical set) ────────────────────────────────────────────────

export const RECIPE_OPEN_MARKS = {
  routeEnter: "recipe.routeEnter",
  rpcStart: "recipe.rpcStart",
  rpcEnd: "recipe.rpcEnd",
  workspaceMount: "recipe.workspaceMount",
  editorMount: "recipe.editorMount",
  grammarFirstParse: "recipe.grammarFirstParse",
  firstPaint: "recipe.firstPaint",
  /**
   * Fired the first time the BodySnapshotOverlay renders cached blocks on a
   * warm open. Measures the "cache → pixels" latency: from `recipe.routeEnter`
   * to this mark should be <100 ms on an L1/L2 cache hit.
   *
   * Not fired on cold opens (no snapshot in cache) — the overlay is absent and
   * the skeleton + y-doc-sync path is used instead.
   */
  bodyCachePaint: "recipe.bodyCachePaint",
  /**
   * Fired the first time cook mode's ReadView renders with non-empty content.
   * Measures the cook-mode default-open latency: from `recipe.routeEnter` to
   * this mark should be <100 ms when the draft body is available from the
   * attempt.list cache (SWR 15s TTL).
   *
   * Not fired when the cached body is empty ("") — nothing useful to measure
   * until the y-doc populates the editor and onContentChange fires.
   */
  cookViewFirstPaint: "recipe.cookViewFirstPaint",
  /**
   * Fired when the live y-doc is hydrated from the WebYjsStorageAdapter IDB
   * cache on a warm open — i.e. `createSyncClient`'s local hydration applied
   * ≥1 persisted update BEFORE the WebSocket handshake (dark perf cache #3).
   *
   * Distinct from `bodyCachePaint`: that mark is the non-authoritative
   * BodySnapshot overlay painting cached blocks; THIS mark is the authoritative
   * live doc being seeded from IDB. Not fired on a cold open (empty cache).
   */
  yjsCacheHydrate: "recipe.yjsCacheHydrate",
  /**
   * Fired on the first `sync(true)` handshake for the draft connection — the
   * point at which the IDB-hydrated (potentially stale) local doc has
   * reconciled with the server's authoritative state. Paired with
   * `yjsCacheHydrate`, the `recipe.yjsReconcileWindow` measure between them is
   * the "editing-on-cache-before-reconcile" exposure window that the freeze
   * action gate (`sendSaveAsVersionOnProvider` waits for `synced`) and the
   * BodySnapshot overlay dissolve both close.
   */
  yjsReconciled: "recipe.yjsReconciled",
} as const;

export type RecipeOpenMark = (typeof RECIPE_OPEN_MARKS)[keyof typeof RECIPE_OPEN_MARKS];

// ── Internal store ────────────────────────────────────────────────────────────

interface PerfEntry {
  /** Mark or measure name. */
  name: string;
  /** "mark" → instantaneous; "measure" → has duration. */
  kind: "mark" | "measure";
  /** ms since `performance.timeOrigin`. */
  startTime: number;
  /** Duration in ms (0 for marks). */
  duration: number;
}

type Listener = (entries: ReadonlyArray<PerfEntry>) => void;

const _state = {
  entries: [] as PerfEntry[],
  listeners: new Set<Listener>(),
  /** Track whether a mark name has already been recorded in this navigation —
   *  preserves "first" semantics so editorMount and grammarFirstParse are
   *  idempotent if their call sites fire more than once. */
  seen: new Set<string>(),
};

function notify(): void {
  const snapshot = _state.entries.slice();
  for (const listener of _state.listeners) {
    listener(snapshot);
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Record a `performance.mark()` at the canonical recipe-open boundary.
 *
 * Idempotent on repeated calls: the first call wins. This matters for marks
 * like `editorMount` whose React effect can fire multiple times during a
 * StrictMode double-render or when a parent re-renders.
 *
 * No-op when `PERF_ENABLED` is false.
 */
export function mark(name: string): void {
  if (!PERF_ENABLED) return;
  if (typeof performance === "undefined" || typeof performance.mark !== "function") return;
  if (_state.seen.has(name)) return;
  _state.seen.add(name);
  try {
    const entry = performance.mark(name);
    _state.entries.push({
      name,
      kind: "mark",
      // PerformanceMark.startTime is ms since timeOrigin.
      startTime: entry?.startTime ?? performance.now(),
      duration: 0,
    });
  } catch {
    // Older browsers / stripped Performance API — fall back to manual entry.
    _state.entries.push({
      name,
      kind: "mark",
      startTime: performance.now(),
      duration: 0,
    });
  }
  notify();
}

/**
 * Record a `performance.measure()` between two marks.
 *
 * If `endMark` is omitted, the measure runs from `startMark` to "now".
 * Returns the measured duration in ms, or `null` if the measure could not be
 * computed (e.g. start mark missing).
 */
export function measure(name: string, startMark: string, endMark?: string): number | null {
  if (!PERF_ENABLED) return null;
  if (typeof performance === "undefined" || typeof performance.measure !== "function") {
    return null;
  }
  try {
    // performance.measure throws if the start mark does not exist.
    const entry = endMark
      ? performance.measure(name, startMark, endMark)
      : performance.measure(name, startMark);
    const startTime = entry?.startTime ?? 0;
    const duration = entry?.duration ?? 0;
    _state.entries.push({
      name,
      kind: "measure",
      startTime,
      duration,
    });
    notify();
    return duration;
  } catch {
    return null;
  }
}

/**
 * Reset the in-memory timeline. Called on route navigation so each recipe-open
 * sweep produces a fresh window. Performance API entries created via
 * `performance.mark()` are NOT cleared from the browser's buffer here — that
 * is intentional: the DevTools Performance panel still shows the full history.
 */
export function reset(): void {
  if (!PERF_ENABLED) return;
  _state.entries = [];
  _state.seen.clear();
  notify();
  // Best-effort clear of mark/measure buffers so a stale `recipe.routeEnter`
  // does not collide with the next navigation's mark of the same name.
  try {
    if (typeof performance !== "undefined") {
      if (typeof performance.clearMarks === "function") {
        for (const m of Object.values(RECIPE_OPEN_MARKS)) {
          performance.clearMarks(m);
        }
      }
      if (typeof performance.clearMeasures === "function") {
        performance.clearMeasures();
      }
    }
    // eslint-disable-next-line no-restricted-syntax -- best-effort: clearing perf marks/measures is telemetry cleanup, not a user-data mutation; fail open (Rule 8 documented degradation).
  } catch {
    // No-op — clearing is best-effort.
  }
}

/** Read-only snapshot of the current entries. */
export function entries(): ReadonlyArray<PerfEntry> {
  return _state.entries.slice();
}

/** Subscribe to entry updates. Returns an unsubscribe fn. */
export function subscribe(listener: Listener): () => void {
  if (!PERF_ENABLED) return () => {};
  _state.listeners.add(listener);
  return () => {
    _state.listeners.delete(listener);
  };
}

// ── Headless capture seam ─────────────────────────────────────────────────────

/**
 * Expose `window.__perf` for headless capture (Playwright, CDP).
 *
 * Idempotent: safe to call multiple times. Only attaches when
 * `PERF_ENABLED` is true and `window` is defined.
 */
export function installWindowSeam(): void {
  if (!PERF_ENABLED) return;
  if (typeof window === "undefined") return;
  // Reuse the existing handle if a previous mount already installed it.
  const w = window as unknown as { __perf?: PerfWindowSeam };
  if (w.__perf) return;
  w.__perf = {
    entries,
    reset,
    subscribe,
  };
}

/** Public shape of `window.__perf`. */
export interface PerfWindowSeam {
  entries: () => ReadonlyArray<PerfEntry>;
  reset: () => void;
  subscribe: (listener: Listener) => () => void;
}

// ── Exported types ────────────────────────────────────────────────────────────

export type { PerfEntry };
